import React, { FC } from 'react';

import { Stack, Text } from '@mantine/core';
import useSWR from 'swr';

import { CollateralHelpers } from 'helpers/CollateralHelpers';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { CollateralTeaserGrid } from 'components/collateral/CollateralTeaserGrid';
import { CollateralEditor } from 'components/collateral-editor/CollateralEditor';
import { DatasheetMantineProviderReset } from 'components/datasheet/DatasheetMantineProviderReset';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { CollateralService } from 'services/CollateralService';

import { useComponentContext } from '../../hooks/use-component-context';
import { ComponentDatasheetSection } from './ComponentDatasheetSection';

const Collateral: FC = () => {
    const mode = useDatasheetMode();

    const { component } = useComponentContext();
    const { company: manufacturer } = useCompanyProfile(component.manufacturer);
    const { data: productCollateral = [], mutate } = useSWR(
        component.id ? CollateralHelpers.swr.byProduct(component.id) : null,
        () => CollateralService.listByProduct(component.id!),
    );

    if (!manufacturer || !component.id) return null;

    const href = (id: string) => CompanyHelpers.urls.collateralItem(manufacturer.slug, id);

    if (mode === DatasheetMode.EDIT) {
        return (
            <ComponentDatasheetSection title="Collateral">
                <Stack gap="lg">
                    <Text size="sm" c="dimmed">
                        Brochures, flyers and videos for this product. They show at the top of the product page, and
                        when visitors scan this product's QR code at RE+, every one they save shows up in your leads.
                    </Text>
                    <DatasheetMantineProviderReset>
                        <CollateralEditor
                            owner={{ product: component.id }}
                            collateral={productCollateral}
                            href={(item) => href(item.id)}
                            onChange={() => mutate()}
                        />
                    </DatasheetMantineProviderReset>
                </Stack>
            </ComponentDatasheetSection>
        );
    }

    const collateral = productCollateral.filter(CollateralHelpers.isViewable);

    if (mode !== DatasheetMode.VIEW || collateral.length === 0) return null;

    return (
        <ComponentDatasheetSection title="Collateral">
            <CollateralTeaserGrid items={collateral.map((item) => ({ collateral: item, href: href(item.id) }))} />
        </ComponentDatasheetSection>
    );
};

export { Collateral };
